import { useMemo } from 'react'
import type { ISuperPromptModel } from '@/lib/db/types'

interface GroupedSuperPrompts {
  isEmpty: boolean
  categories: string[]
  groupedPrompts: Record<string, ISuperPromptModel[]>
}

export function useSuperPromptsGrouping(
  superPrompts: ISuperPromptModel[],
  search: string = '',
): GroupedSuperPrompts {
  return useMemo(() => {
    const priorityCategories = ['Embed', 'AI API']

    // Filter published and non-hidden prompts
    let filteredPrompts = superPrompts.filter(
      (prompt) =>
        prompt.status === 'published' && !prompt.hidden_in_menu_and_will_detect,
    )

    // Apply search filter if search term exists
    if (search.trim()) {
      const searchLower = search.toLowerCase()
      filteredPrompts = filteredPrompts.filter((prompt) => {
        return (
          prompt.name?.toLowerCase().includes(searchLower) ||
          prompt.search_name?.toLowerCase().includes(searchLower) ||
          prompt.description?.toLowerCase().includes(searchLower) ||
          prompt.category?.toLowerCase().includes(searchLower)
        )
      })
    }

    if (filteredPrompts.length === 0) {
      return { isEmpty: true, categories: [], groupedPrompts: {} }
    }

    // Group by category
    const groupedPrompts = filteredPrompts.reduce(
      (groups, prompt) => {
        const category = prompt.category || 'others'
        if (!groups[category]) {
          groups[category] = []
        }
        groups[category].push(prompt)
        return groups
      },
      {} as Record<string, ISuperPromptModel[]>,
    )

    // Sort Embed category in reverse alphabetical order by name
    if (groupedPrompts.Embed) {
      groupedPrompts.Embed.sort((a, b) => b.name.localeCompare(a.name))
    }

    // Get all categories, prioritize Embed and AI API
    const allCategories = Object.keys(groupedPrompts)
    const otherCategories = allCategories
      .filter((cat) => !priorityCategories.includes(cat))
      .sort()
    const categories = [
      ...priorityCategories.filter((cat) => groupedPrompts[cat]),
      ...otherCategories,
    ]

    return {
      isEmpty: false,
      categories,
      groupedPrompts,
    }
  }, [superPrompts, search])
}
